import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  DashboardApi,
  type BacklogItemView,
  type RecommendationView,
} from "./api/client.js";
import {
  ModeSelector,
  type OperationsMode,
  PrototypeBanner,
} from "./components/mode-selector.js";
import {
  SportSelector,
  type DashboardSport,
} from "./components/sport-selector.js";
import { SectionRail } from "./components/section-rail.js";
import { PhoneAlerts } from "./components/phone-alerts.js";
import { HomePage } from "./pages/home.js";
import { HealthPage } from "./pages/health.js";
import { AnalyticsPage } from "./pages/analytics.js";
import { LedgerPage } from "./pages/ledger.js";
import { AccountsPage } from "./pages/accounts.js";
import { IdeasPage } from "./pages/ideas.js";
import { RulesPage } from "./pages/rules.js";
import { CalendarPage } from "./pages/calendar.js";
import { TodayPage } from "./pages/today.js";
import {
  confirmPrototypeLineup,
  createPrototypeSession,
  placePrototypeBet,
  prototypeCsv,
  prototypeAnalytics,
  prototypeDashboardSnapshot,
  settlePrototypeBet,
} from "./prototype/session.js";

type Route =
  | "home"
  | "today"
  | "ledger"
  | "accounts"
  | "analytics"
  | "rules"
  | "calendar"
  | "health"
  | "ideas";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "http://127.0.0.1:3000";

/**
 * A Discord alert links straight to the bet it is about, so logging it is two
 * taps instead of opening the dashboard and hunting for the right card among
 * however many are live. Read at render, not at module load, so the target
 * survives a sign-in: an expired session shows the password screen and the
 * bet is still waiting behind it.
 */
function placementTargetFromUrl(): {
  key: string;
  sport: DashboardSport;
} | null {
  const params = new URLSearchParams(location.search);
  const key = params.get("place");
  if (key === null || key === "") return null;
  const sport = params.get("sport");
  return {
    key,
    sport:
      sport === "MLB" ||
      sport === "NHL" ||
      sport === "NFL" ||
      sport === "NBA" ||
      sport === "SOCCER" ||
      sport === "NBL"
        ? sport
        : "NBA",
  };
}

/** Drop the parameter once acted on, so a refresh does not reopen the dialog
 * for a bet that has already been logged. */
function clearPlacementTargetFromUrl(): void {
  const url = new URL(location.href);
  url.searchParams.delete("place");
  url.searchParams.delete("sport");
  history.replaceState(null, "", url.toString());
}

function routeFromPath(): Route {
  return location.pathname === "/ledger"
    ? "ledger"
    : location.pathname === "/health"
      ? "health"
      : location.pathname === "/calendar"
        ? "calendar"
        : location.pathname === "/rules"
          ? "rules"
          : location.pathname === "/analytics"
            ? "analytics"
            : location.pathname === "/today"
              ? "today"
              : location.pathname === "/accounts"
                ? "accounts"
                : location.pathname === "/ideas"
                  ? "ideas"
                  : "home";
}

interface OperatorSession {
  readonly token: string;
  readonly expiresAt: string;
}

/**
 * The session lives in localStorage, not sessionStorage.
 *
 * sessionStorage is cleared whenever the tab closes, so every new tab and
 * every browser restart forced another sign-in - many times a day. The token
 * is signed server-side and already expires after twelve hours, so persisting
 * it does not extend how long it is valid; it only stops the operator having
 * to re-enter the password to use a credential that is still good.
 *
 * Both stores are equally exposed to XSS, so this trades no additional
 * script-access risk - only durability on the device.
 */
const SESSION_KEY = "nba-ops-session";

function readStoredSession(): OperatorSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<OperatorSession>;
    if (
      typeof parsed.token !== "string" ||
      typeof parsed.expiresAt !== "string"
    )
      return null;
    // Drop an expired token here rather than letting every request 401 and
    // leave the operator on a broken page with no obvious way back.
    if (Date.parse(parsed.expiresAt) <= Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return { token: parsed.token, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
}

function writeStoredSession(session: OperatorSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Private browsing can refuse writes. Staying signed in for this tab is
    // still better than failing the sign-in outright.
  }
}

function clearStoredSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
    // Clear the pre-localStorage key too, so an old tab cannot resurrect it.
    sessionStorage.removeItem("nba-ops-token");
  } catch {
    // Nothing to do: the session is already unreadable.
  }
}

async function signIn(password: string): Promise<OperatorSession> {
  const response = await fetch(`${apiBaseUrl}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  const body = (await response.json()) as {
    token?: unknown;
    expiresAt?: unknown;
  };
  if (!response.ok || typeof body.token !== "string")
    throw new Error("Password not accepted");
  return {
    token: body.token,
    expiresAt:
      typeof body.expiresAt === "string"
        ? body.expiresAt
        : new Date(Date.now() + 12 * 60 * 60_000).toISOString(),
  };
}

function SignIn({
  onSignedIn,
}: {
  onSignedIn: (session: OperatorSession) => void;
}) {
  const [password, setPassword] = useState("");
  const login = useMutation({
    mutationFn: () => signIn(password),
    onSuccess: onSignedIn,
  });
  return (
    <main className="sign-in-shell">
      <form
        className="card sign-in-card"
        onSubmit={(event) => {
          event.preventDefault();
          login.mutate();
        }}
      >
        <span className="brand-mark">E</span>
        <span className="eyebrow">Private operations</span>
        <h1>Edge Ops</h1>
        <p>Sign in to view qualified markets and log placements.</p>
        <label>
          Password
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        {login.error === null ? null : (
          <p className="negative" role="alert">
            {login.error.message}
          </p>
        )}
        <button className="button primary" disabled={login.isPending}>
          {login.isPending ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
}

/**
 * Prototype mode is requested by URL so it can be reached when the API is
 * down - the situation it exists for - and so the board can be linked to.
 */
function prototypeRequested(): boolean {
  return (
    new URLSearchParams(window.location.search).get("mode") === "prototype"
  );
}

function Operations({
  token,
  onSignOut,
}: {
  token: string;
  onSignOut: () => void;
}) {
  const [placementTarget, setPlacementTarget] = useState(
    placementTargetFromUrl,
  );
  const [route, setRoute] = useState<Route>(routeFromPath);
  // Prototype mode exists to demonstrate the board without touching live
  // services, but the toggle only renders once live data has loaded - so it
  // was unreachable in exactly the situation it is for, an API that is down.
  // Reading it from the URL makes it reachable, and gives a link worth sharing.
  const [mode, setMode] = useState<OperationsMode>(() =>
    prototypeRequested() ? "prototype" : "live",
  );
  // A deep-linked bet decides the sport: an MLB alert opened on the NBA tab
  // would show nothing at all.
  // Every sport, not NBA. Signing in during the MLB season and being shown an
  // empty NBA board says the system is idle when it is working.
  const [sport, setSport] = useState<DashboardSport | "ALL">(
    placementTarget?.sport ?? "ALL",
  );
  // Ledger, health and exports read one sport. Only the board combines them,
  // so anywhere else falls back to a concrete sport rather than widening its
  // own query.
  const singleSport: DashboardSport = sport === "ALL" ? "MLB" : sport;
  const [prototypeSession, setPrototypeSession] = useState(
    createPrototypeSession,
  );
  const [clock, setClock] = useState("");
  const queryClient = useQueryClient();
  const api = useMemo(
    () => new DashboardApi(apiBaseUrl, () => token, onSignOut),
    [onSignOut, token],
  );
  const dashboard = useQuery({
    queryKey: ["dashboard", sport],
    queryFn: () => api.dashboard(sport),
    refetchInterval: 15_000,
  });
  const combinedSports: readonly DashboardSport[] =
    sport === "ALL" ? ["MLB", "NBA", "NHL", "NFL", "SOCCER", "NBL"] : [];
  const combinedLedgers = useQueries({
    queries: combinedSports.map((item) => ({
      queryKey: ["ledger", item] as const,
      queryFn: () => api.ledger(item),
      enabled:
        sport === "ALL" &&
        (route === "ledger" || route === "home") &&
        mode === "live",
      refetchInterval: 60_000,
    })),
  });
  const ledger = useQuery({
    queryKey: ["ledger", singleSport],
    queryFn: () => api.ledger(singleSport),
    enabled:
      sport !== "ALL" &&
      (route === "ledger" || route === "home") &&
      mode === "live",
    refetchInterval: 60_000,
  });
  // Tracked rules, fetched only for the ledger page and kept apart from the
  // official rows everywhere.
  const trackedLedgers = useQueries({
    queries: (sport === "ALL" ? combinedSports : ([singleSport] as const)).map(
      (item) => ({
        queryKey: ["tracked-ledger", item] as const,
        queryFn: () => api.trackedLedger(item),
        enabled: route === "ledger" && mode === "live",
        refetchInterval: 300_000,
      }),
    ),
  });
  const trackedRows = trackedLedgers.flatMap((query) => query.data?.data ?? []);
  // The ledger page and the home board read the same rows.
  const ledgerRows =
    mode === "prototype"
      ? prototypeSession.ledger.map((row) => ({ ...row }))
      : sport === "ALL"
        ? combinedLedgers.flatMap((query) => query.data?.data ?? [])
        : (ledger.data?.data ?? []);
  // Analytics spans every sport by default: this is one system, so a sport is
  // a filter rather than the scope.
  const [analyticsScope, setAnalyticsScope] = useState<"ALL" | typeof sport>(
    "ALL",
  );
  const analytics = useQuery({
    queryKey: ["analytics", analyticsScope],
    queryFn: () => api.analytics(analyticsScope),
    // Home reads the whole-market agreement figure from the same rollup.
    enabled: (route === "analytics" || route === "home") && mode === "live",
    refetchInterval: 60_000,
  });
  const pendingLineups = useQuery({
    queryKey: ["pending-lineups"],
    queryFn: () => api.pendingLineups(),
    enabled: mode === "live" && sport === "NBA",
    refetchInterval: 15_000,
  });
  const betWindows = useQuery({
    queryKey: ["bet-windows"],
    queryFn: () => api.betWindows(),
    enabled: route === "calendar" && mode === "live",
    refetchInterval: 600_000,
  });
  const ruleHealth = useQuery({
    queryKey: ["rule-health"],
    queryFn: () => api.ruleHealth(),
    enabled: route === "rules" && mode === "live",
    refetchInterval: 300_000,
  });
  const backlog = useQuery({
    queryKey: ["backlog"],
    queryFn: () => api.backlog(),
    enabled: route === "ideas" && mode === "live",
  });
  // Book exposure is sport-agnostic: the accounts are shared across every
  // sport, and limiting at one book removes bets from all of them.
  const books = useQuery({
    queryKey: ["books"],
    queryFn: () => api.books(),
    // Also on Today, because that is where a bet is placed and the routing
    // is only worth anything at the moment the book is chosen.
    enabled: (route === "accounts" || route === "today") && mode === "live",
    refetchInterval: 60_000,
  });
  const health = useQuery({
    queryKey: ["health", singleSport],
    queryFn: () => api.health(singleSport),
    enabled: (route === "health" || route === "home") && mode === "live",
    refetchInterval: 30_000,
  });
  useEffect(() => {
    const update = () =>
      setClock(
        new Intl.DateTimeFormat("en-AU", {
          timeZone: "Australia/Melbourne",
          hour: "numeric",
          minute: "2-digit",
          timeZoneName: "short",
        }).format(new Date()),
      );
    update();
    const timer = setInterval(update, 30_000);
    return () => clearInterval(timer);
  }, []);
  const navigate = (next: Route) => {
    // Home owns "/", which is where signing in lands. Today had it, while
    // routeFromPath read "/" as home - so the address bar and the page
    // disagreed and a refresh on Today silently moved you to Home.
    history.pushState({}, "", next === "home" ? "/" : `/${next}`);
    setRoute(next);
  };
  if (mode === "live" && dashboard.error !== null)
    return (
      <main className="loading-state">
        <p>Operations data could not be loaded.</p>
        <button className="button secondary" onClick={onSignOut}>
          Sign in again
        </button>
      </main>
    );
  if (mode === "live" && (dashboard.isLoading || dashboard.data === undefined))
    return <main className="loading-state">Loading operations…</main>;
  const snapshot =
    mode === "prototype"
      ? prototypeDashboardSnapshot(prototypeSession)
      : dashboard.data!;
  return (
    <div className="app-shell">
      <SectionRail route={route} onNavigate={navigate} />
      <nav className="topbar">
        <button
          className="brand"
          type="button"
          onClick={() => navigate("today")}
        >
          <span className="brand-mark">{sport[0]}</span>
          <strong>{sport} Edge Ops</strong>
        </button>
        <SportSelector
          sport={sport}
          // Home, the board and the ledger all combine sports: the queue, the
          // money and the records are each answerable across four of them.
          // Health stays pinned, because its components are per sport and a
          // merged list would not say which sport was unhealthy.
          includeAll={
            (route === "home" || route === "today" || route === "ledger") &&
            mode === "live"
          }
          disabled={mode === "prototype"}
          onChange={setSport}
        />
        <ModeSelector
          mode={mode}
          onChange={(next) => {
            if (next === "prototype") setSport("NBA");
            setMode(next);
          }}
        />
        {mode === "live" ? (
          <PhoneAlerts
            onSubscribe={(subscription) => api.subscribePhone(subscription)}
          />
        ) : null}
        <div className="melbourne-clock">
          <span>Melbourne time</span>
          <strong>{clock}</strong>
        </div>
      </nav>
      <main>
        {mode === "prototype" ? <PrototypeBanner /> : null}
        {route === "home" ? (
          <HomePage
            snapshot={snapshot}
            ledger={ledgerRows}
            health={
              mode === "prototype" ? prototypeSession.health : health.data
            }
            sport={sport}
            feedSport={singleSport}
            onOpenBoard={() => navigate("today")}
            books={books.data?.data ?? []}
            marketAgreement={(() => {
              const overall = analytics.data?.data.find(
                (row) =>
                  !row.rule_scoped &&
                  !row.bookmaker_scoped &&
                  (analyticsScope === "ALL"
                    ? !row.sport_scoped
                    : row.sport_scoped),
              );
              return overall === undefined
                ? undefined
                : {
                    toward: overall.market_toward ?? 0,
                    against: overall.market_against ?? 0,
                  };
            })()}
            onPlace={(key) => {
              setPlacementTarget({ key, sport: singleSport });
              navigate("today");
            }}
          />
        ) : route === "today" ? (
          <TodayPage
            key={mode}
            sport={sport}
            openPlacementFor={placementTarget?.key ?? null}
            onPlacementOpened={() => {
              clearPlacementTargetFromUrl();
              setPlacementTarget(null);
            }}
            snapshot={snapshot}
            books={books.data?.data ?? []}
            {...(mode === "prototype"
              ? {
                  prototype: true,
                  lineups: prototypeSession.lineups,
                  onLineupDecision: (
                    candidateId: string,
                    status: "CONFIRMED_STARTER" | "CONFIRMED_NON_STARTER",
                  ) => {
                    const result = confirmPrototypeLineup(prototypeSession, {
                      candidateId,
                      status,
                    });
                    if (result.ok) setPrototypeSession(result.session);
                  },
                }
              : {})}
            {...(mode === "live"
              ? {
                  lineups:
                    sport === "NBA" ? (pendingLineups.data?.data ?? []) : [],
                  onLineupDecision: (
                    candidateId: string,
                    status: "CONFIRMED_STARTER" | "CONFIRMED_NON_STARTER",
                  ) => {
                    void (async () => {
                      const candidate = pendingLineups.data?.data.find(
                        (item) => item.candidateId === candidateId,
                      );
                      if (candidate === undefined) return;
                      await api.confirmLineup({
                        gameId: candidate.gameId,
                        playerId: candidate.playerId,
                        status,
                        source: "MANUAL_DASHBOARD",
                      });
                      await Promise.all([
                        queryClient.invalidateQueries({
                          queryKey: ["pending-lineups"],
                        }),
                        queryClient.invalidateQueries({
                          queryKey: ["dashboard", sport],
                        }),
                      ]);
                    })();
                  },
                }
              : {})}
            {...(mode === "live"
              ? {
                  onSkipBet: (recommendation: RecommendationView) => {
                    void (async () => {
                      await api.skipRecommendation(recommendation.signalId);
                      await queryClient.invalidateQueries({
                        queryKey: ["dashboard", sport],
                      });
                    })();
                  },
                }
              : {})}
            onPlaceBet={async (recommendation, input) => {
              if (mode === "prototype") {
                const result = placePrototypeBet(prototypeSession, {
                  signalId: recommendation.signalId,
                  ...input,
                });
                if (result.ok) setPrototypeSession(result.session);
                return;
              }
              await api.placeBet({
                signalId: recommendation.signalId,
                // The operator chooses the book in the dialog: they may have
                // taken the bet somewhere other than the best quote we held.
                ...input,
              });
              // The bet is logged once placeBet returns. The board reload runs
              // behind the closed dialog rather than holding it open.
              void queryClient.invalidateQueries({
                queryKey: ["dashboard", sport],
              });
            }}
          />
        ) : route === "calendar" ? (
          <CalendarPage days={betWindows.data?.data ?? []} />
        ) : route === "rules" ? (
          <RulesPage rows={ruleHealth.data?.data ?? []} />
        ) : route === "ideas" ? (
          <IdeasPage
            items={backlog.data?.data ?? []}
            {...(mode === "live"
              ? {
                  onAdd: (input: { title: string; note: string | null }) => {
                    void (async () => {
                      await api.addBacklogItem(input);
                      await queryClient.invalidateQueries({
                        queryKey: ["backlog"],
                      });
                    })();
                  },
                  onStatus: (id: string, status: BacklogItemView["status"]) => {
                    void (async () => {
                      await api.setBacklogStatus({ id, status });
                      await queryClient.invalidateQueries({
                        queryKey: ["backlog"],
                      });
                    })();
                  },
                }
              : {})}
          />
        ) : route === "accounts" ? (
          <AccountsPage
            books={books.data?.data ?? []}
            {...(books.data?.funding === undefined
              ? {}
              : { funding: books.data.funding })}
            onStatusChange={(bookmaker, status) => {
              void (async () => {
                await api.setBookStatus({ bookmaker, status });
                await queryClient.invalidateQueries({ queryKey: ["books"] });
              })();
            }}
            onFunds={(input) => {
              void (async () => {
                await api.recordBookTransaction(input);
                await queryClient.invalidateQueries({ queryKey: ["books"] });
              })();
            }}
          />
        ) : route === "ledger" ? (
          <LedgerPage
            key={mode}
            rows={ledgerRows}
            trackedRows={mode === "live" ? trackedRows : []}

            {...(mode === "prototype" ? { exportUrl: "#prototype" } : {})}
            {...(mode === "live"
              ? {
                  onExport: () => void api.exportPlaced(singleSport),
                  onConfirmRefund: async (betId: string) => {
                    await api.confirmRefund(betId);
                    await queryClient.invalidateQueries({
                      queryKey: ["ledger"],
                    });
                  },
                  onAmend: async (input: {
                    sport: string;
                    betId: string;
                    bookmaker?: string;
                    actualPrice?: number;
                  }) => {
                    await api.amendBet(input);
                    // The ledger, the exposure and the day's profit all move
                    // with a correction, so refetch rather than patching one
                    // row and leaving the rest disagreeing with it.
                    await queryClient.invalidateQueries();
                  },
                }
              : {})}
            {...(mode === "prototype"
              ? {
                  prototype: true,
                  onSettle: (betId: string) => {
                    const result = settlePrototypeBet(prototypeSession, betId);
                    if (result.ok) setPrototypeSession(result.session);
                  },
                  onExport: () => {
                    const blob = new Blob(
                      [prototypeCsv(prototypeSession.ledger)],
                      { type: "text/csv;charset=utf-8" },
                    );
                    const url = URL.createObjectURL(blob);
                    const anchor = document.createElement("a");
                    anchor.href = url;
                    anchor.download = "nba-edge-prototype-ledger.csv";
                    anchor.click();
                    URL.revokeObjectURL(url);
                  },
                }
              : {})}
          />
        ) : route === "analytics" ? (
          <>
            <div
              className="sport-selector"
              role="group"
              aria-label="Analytics sport"
            >
              {(
                ["ALL", "MLB", "NBA", "NHL", "NFL", "SOCCER", "NBL"] as const
              ).map((item) => (
                <button
                  key={item}
                  type="button"
                  aria-pressed={analyticsScope === item}
                  onClick={() => setAnalyticsScope(item)}
                >
                  {item === "ALL" ? "All sports" : item}
                </button>
              ))}
            </div>
            <AnalyticsPage
              rows={
                mode === "prototype"
                  ? prototypeAnalytics(prototypeSession.ledger)
                  : (analytics.data?.data ?? [])
              }
              sport={analyticsScope}
              loadRule={
                mode === "live"
                  ? async (ruleSport, rule) =>
                      (await api.ruleHistory(ruleSport, rule)).data
                  : undefined
              }
            />
          </>
        ) : (
          <HealthPage
            key={mode}
            sport={singleSport}
            {...(mode === "prototype"
              ? { health: prototypeSession.health, prototype: true }
              : health.data === undefined
                ? {}
                : { health: health.data })}
          />
        )}
      </main>
    </div>
  );
}

export function App() {
  const [session, setSession] = useState(readStoredSession);
  // Prototype mode renders synthetic fixtures and calls no service, so a
  // password in front of it protects nothing - there is no real bet, price or
  // bankroll behind it. Requiring one only made the board impossible to look
  // at, which is the one thing prototype mode is for. Live mode is untouched
  // and still needs a session for every request.
  if (session === null && !prototypeRequested())
    return (
      <SignIn
        onSignedIn={(value) => {
          writeStoredSession(value);
          setSession(value);
        }}
      />
    );
  return (
    <Operations
      token={session?.token ?? ""}
      onSignOut={() => {
        clearStoredSession();
        setSession(null);
      }}
    />
  );
}
